import apiService from '../services/apiService';
import Enquiry from '../models/Enquiry';

export const enquiryController = {
  // Get all enquiries with optional filters
  getAll: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const endpoint = queryParams ? `enquiries?${queryParams}` : 'enquiries';
      const response = await apiService.get(endpoint);
      const enquiries = response.data?.map(e => Enquiry.fromJSON(e)) || [];
      return { success: true, data: enquiries, total: response.total || enquiries.length };
    } catch (error) {
      return { success: false, data: [], message: error.message || 'Failed to fetch enquiries' };
    }
  },

  // Get enquiry by ID
  getById: async (id) => {
    try {
      const response = await apiService.get(`enquiries/${id}`);
      return { success: true, data: Enquiry.fromJSON(response.data) };
    } catch (error) {
      return { success: false, data: null, message: error.message };
    }
  },

  // Create new enquiry
  create: async (enquiryData) => {
    try {
      const enquiry = new Enquiry(enquiryData);
      const validation = enquiry.validate();
      if (!validation.isValid) return { success: false, errors: validation.errors };
      const response = await apiService.post('enquiries', enquiry.toJSON());
      return { success: true, data: Enquiry.fromJSON(response.data), message: 'Enquiry created successfully' };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Update existing enquiry
  update: async (id, enquiryData) => {
    try {
      const enquiry = new Enquiry({ ...enquiryData, id });
      const validation = enquiry.validate();
      if (!validation.isValid) return { success: false, errors: validation.errors };
      const response = await apiService.put(`enquiries/${id}`, enquiry.toJSON());
      return { success: true, data: Enquiry.fromJSON(response.data), message: 'Enquiry updated successfully' };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Delete enquiry
  delete: async (id) => {
    try {
      await apiService.delete(`enquiries/${id}`);
      return { success: true, message: 'Enquiry deleted successfully' };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Update enquiry status
  updateStatus: async (id, status) => {
    try {
      const response = await apiService.patch(`enquiries/${id}/status`, { status });
      return { success: true, data: Enquiry.fromJSON(response.data), message: 'Status updated successfully' };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Assign enquiry to user
  assign: async (id, userId) => {
    try {
      const response = await apiService.patch(`enquiries/${id}/assign`, { userId });
      return { success: true, data: Enquiry.fromJSON(response.data), message: 'Enquiry assigned successfully' };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Convert enquiry to lead
  convertToLead: async (id, leadData) => {
    try {
      const response = await apiService.post(`enquiries/${id}/convert`, leadData);
      return { success: true, data: response.data, message: 'Enquiry converted to lead successfully' };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Add follow-up note
  addFollowUp: async (id, note) => {
    try {
      const response = await apiService.post(`enquiries/${id}/followup`, { note, date: new Date().toISOString() });
      return { success: true, data: Enquiry.fromJSON(response.data), message: 'Follow-up added successfully' };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Get pending enquiries
  getPending: async () => {
    return await enquiryController.getAll({ status: 'Pending' });
  },

  // Get follow-up required enquiries
  getFollowUpRequired: async () => {
    return await enquiryController.getAll({ status: 'Follow-up' });
  },

  // Get converted enquiries
  getConverted: async () => {
    return await enquiryController.getAll({ status: 'Converted' });
  },

  // Export enquiries
  export: async (filters = {}, format = 'csv') => {
    try {
      const queryParams = new URLSearchParams({ ...filters, format }).toString();
      const response = await apiService.get(`enquiries/export?${queryParams}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Get statistics
  getStatistics: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const endpoint = queryParams ? `enquiries/statistics?${queryParams}` : 'enquiries/statistics';
      const response = await apiService.get(endpoint);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, data: null, message: error.message };
    }
  }
};

export default enquiryController;